/**
 * The jade economy: the item mall (ร้านหยก, data/mall.ts), paid top-ups and their milestones
 * (data/topup.ts), and the battle pass (ตั๋วยุทธภพ, data/pass.ts). Also `grantReward`, the one way
 * such rewards (and the arena's and ladders') are paid. The World keeps `creditTopUp` as its public
 * face of this system.
 */
import { MALL } from '../data/mall';
import { MOUNTS } from '../data/mounts';
import { PASS_TIERS, passLevel, SEASON, type PassReward, type PassTrack } from '../data/pass';
import { SKINS } from '../data/skins';
import { TITLES } from '../data/titles';
import {
  TOPUP_MILESTONES,
  TOPUP_PACKS,
  TOPUP_RECEIPTS_KEPT,
  topUpJade,
  type TopUpReceipt,
} from '../data/topup';
import type { SystemHost, WorldSystem } from './types';

export class StoreSystem implements WorldSystem {
  readonly name = 'store';

  constructor(private readonly h: SystemHost) {}

  readonly commands = {
    mallBuy: (c: { kind: 'skin' | 'mount'; id: string }) => this.mallBuy(c.kind, c.id),
    topUpClaim: (c: { baht: number }) => this.topUpClaim(c.baht),
    passBuy: () => this.passBuy(),
    passClaim: (c: { level: number; track: PassTrack }) => {
      if (this.passClaim(c.level, c.track)) this.h.emit({ type: 'progressChanged' });
    },
    passClaimAll: () => {
      let n = 0;
      for (const t of PASS_TIERS)
        for (const track of ['free', 'premium'] as const) if (this.passClaim(t.level, track, true)) n++;
      if (!n) this.h.notice('ไม่มีรางวัลให้รับ');
      else {
        this.h.notice(`รับรางวัลตั๋วยุทธภพ ${n} รายการ`);
        this.h.emit({ type: 'progressChanged' });
      }
    },
  };

  /**
   * Credits a paid top-up. Deliberately not a PlayerCommand: only the payment side may call it (the
   * sandbox checkout for now, the payment provider's webhook on the server in phase 2). Refuses an
   * unknown pack, an amount that does not match its price, and a receipt already credited.
   */
  creditTopUp(r: TopUpReceipt): boolean {
    const pr = this.h.world.progress;
    const t = pr.topUp;
    const pack = TOPUP_PACKS.find((k) => k.id === r.pack);
    if (!pack || pack.baht !== r.baht || !r.id || t.receipts.includes(r.id)) return false;
    const first = !t.packs.includes(pack.id);
    const jade = topUpJade(pack, first);
    pr.jade += jade;
    t.baht += pack.baht;
    if (first) t.packs.push(pack.id);
    t.receipts.push(r.id);
    if (t.receipts.length > TOPUP_RECEIPTS_KEPT)
      t.receipts.splice(0, t.receipts.length - TOPUP_RECEIPTS_KEPT);
    this.h.notice(
      `เติมสำเร็จ ได้รับหยกเซียน ${jade.toLocaleString('th-TH')}${first ? ' (เติมครั้งแรก x2)' : ''}`,
    );
    this.h.emit({ type: 'progressChanged' });
    return true;
  }

  /**
   * Pays a reward (top-up milestone or pass tier) and describes it. A look already owned is paid
   * back in jade: its mall price, else 500.
   */
  grantReward(m: PassReward): string {
    const w = this.h.world;
    const pr = w.progress;
    let jade = m.jade ?? 0;
    const got: string[] = [];
    for (const [kind, id] of [
      ['skin', m.skin],
      ['mount', m.mount],
    ] as const) {
      if (!id) continue;
      const owned = kind === 'skin' ? pr.skins : pr.mounts;
      if (owned.includes(id)) jade += MALL.find((e) => e.kind === kind && e.id === id)?.price ?? 500;
      else {
        owned.push(id);
        got.push(`「${kind === 'skin' ? SKINS[id]!.name : MOUNTS[id]!.name}」`);
      }
    }
    if (m.title && !pr.titles.includes(m.title)) {
      w.grantTitle(m.title);
      got.push(`ฉายา「${TITLES[m.title]!.name}」`);
    }
    pr.jade += jade;
    pr.gold += m.gold ?? 0;
    pr.cultivation += m.cultivation ?? 0;
    if (jade) got.push(`หยกเซียน ${jade.toLocaleString('th-TH')}`);
    if (m.gold) got.push(`${m.gold.toLocaleString('th-TH')} ตำลึง`);
    if (m.cultivation) got.push(`แต้มฝึกปรือ ${m.cultivation.toLocaleString('th-TH')}`);
    return got.join(' ');
  }

  /** Season XP; announces each tier reached. */
  passXp(n: number): void {
    const p = this.h.world.progress.pass;
    const before = passLevel(p.xp);
    p.xp = Math.min(SEASON.maxLevel * SEASON.xpPerLevel, p.xp + n);
    const after = passLevel(p.xp);
    if (after > before) {
      this.h.notice(`ตั๋วยุทธภพเลื่อนเป็นขั้น ${after} รับรางวัลได้ที่เมนูตั๋ว (P)`);
      this.h.emit({ type: 'progressChanged' });
    }
  }

  /** The item mall: anywhere, for jade; a look is bought once. */
  private mallBuy(kind: 'skin' | 'mount', id: string): void {
    const pr = this.h.world.progress;
    const e = MALL.find((x) => x.kind === kind && x.id === id);
    if (!e) return;
    const owned = kind === 'skin' ? pr.skins : pr.mounts;
    if (owned.includes(id)) return this.h.notice('มีอยู่แล้ว');
    if (pr.jade < e.price) return this.h.notice('หยกเซียนไม่พอ');
    pr.jade -= e.price;
    owned.push(id);
    this.h.notice(`ได้รับ「${kind === 'skin' ? SKINS[id]!.name : MOUNTS[id]!.name}」จากร้านหยก`);
    this.h.emit({ type: 'progressChanged' });
  }

  /** A top-up milestone reward; a look already owned is paid back in jade (its mall price, else 500). */
  private topUpClaim(baht: number): void {
    const pr = this.h.world.progress;
    const m = TOPUP_MILESTONES.find((x) => x.baht === baht);
    if (!m || pr.topUp.claimed.includes(baht)) return;
    if (pr.topUp.baht < baht) return this.h.notice('ยอดเติมสะสมยังไม่ถึง');
    pr.topUp.claimed.push(baht);
    this.h.notice(`รางวัลยอดเติม ${baht.toLocaleString('th-TH')} บาท: ${this.grantReward(m)}`);
    this.h.emit({ type: 'progressChanged' });
  }

  private passBuy(): void {
    const pr = this.h.world.progress;
    if (pr.pass.premium) return this.h.notice('ปลดล็อกตั๋วพรีเมียมแล้ว');
    if (pr.jade < SEASON.premiumPrice) return this.h.notice('หยกเซียนไม่พอ');
    pr.jade -= SEASON.premiumPrice;
    pr.pass.premium = true;
    this.h.notice(`ปลดล็อกตั๋วยุทธภพพรีเมียม ${SEASON.name}`);
    this.h.emit({ type: 'progressChanged' });
  }

  /** Takes one tier's reward on a track if it is reached, unlocked and not yet taken. */
  private passClaim(level: number, track: PassTrack, quiet = false): boolean {
    const p = this.h.world.progress.pass;
    const tier = PASS_TIERS.find((t) => t.level === level);
    if (!tier || p.claimed[track].includes(level) || passLevel(p.xp) < level) return false;
    if (track === 'premium' && !p.premium) {
      if (!quiet) this.h.notice('ต้องปลดล็อกตั๋วพรีเมียมก่อน');
      return false;
    }
    p.claimed[track].push(level);
    if (!quiet) this.h.notice(`ตั๋วยุทธภพขั้น ${level}: ${this.grantReward(tier[track])}`);
    else this.grantReward(tier[track]);
    return true;
  }
}
